import React from "react";
import {
	SEGMENT_BZA,
	URL_BZA_AIRCRAFT_DATA,
	URL_BZA_AIRCRAFT_HISTORY_DATA,
	URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
	URL_BZA_OPERATOR_AIRPORT_DATA,
} from "../constants";
import { Faq } from "../sections/faq/Faq";
import { Hero } from "../sections/hero/Hero";
import { Pricing } from "../sections/pricing/Pricing";
import { ProductSlider } from "../sections/ProductSlider";
import { ProductTabs } from "../sections/ProductTabs";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { Trial } from "../sections/Trial";
import { Upgrades } from "../sections/Upgrades";
import { productSegmentPageProps } from "../../common/types/product";

export default function BusinessOperatorContactData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Access detailed contact information for active business jet operators worldwide, including 14,000+ senior management personnel."
				packages={[
					{
						label: SEGMENT_BZA,
						features: [
							"14,000+ senior management contacts worldwide",
							"Stay up-to-date with management changes news updates",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Operator Contacts",
						description:
							"List of all senior manager contacts within the operator, including their:",
						list: [
							"Names",
							"Job titles",
							"Email addresses",
							"LinkedIn profiles",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-contact-data/operator-profile-contacts.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for contacts by company, location, first or last name, or based on the operator’s fleet and engine types."
				slides={[
					{
						title: "Company",
						description:
							"Search for contacts within a specific operator, filter them by:",
						descriptionBottom:
							"or load your favourites list to view the contacts.",
						list: ["Operator type", "Operator status"],
						visual: {
							title: "List of all Pilots within the active business/private charter operators",
							url: "/static/img/product/business-jet-operator-contact-data/contact-search-by-company.jpg",
						},
					},
					{
						title: "Location",
						description:
							"Search for contacts within operators by location:",
						list: [
							"Region",
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
							"Home base/airport",
						],
						visual: {
							title: "List of all executives in North America with Corporate Pilot in their job title",
							url: "/static/img/product/business-jet-operator-contact-data/contact-search-by-location.jpg",
						},
					},
					{
						title: "Contact",
						description: "Search for contacts directly by:",
						descriptionBottom:
							"and filter to show only those with email or LinkedIn profiles included.",
						list: [
							"First name",
							"Last name",
							"Job title",
							"Job group",
						],
						visual: {
							title: "List of all Chief Executive Officers, with their emails available",
							url: "/static/img/product/business-jet-operator-contact-data/contact-search-by-contact.jpg",
						},
					},
					{
						title: "Fleet",
						description:
							"Search for contacts within operators based on fleet parameters, including:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft family/type/variant",
							"Fleet size",
							"Total seats",
							"Aircraft seat range",
						],
						visual: {
							title: "Directory of Aircraft Managers at operators managing Gulfstream aircraft",
							url: "/static/img/product/business-jet-operator-contact-data/contact-search-by-fleet.jpg",
						},
					},
					{
						title: "Engines",
						description:
							"Search for contacts within operators based on the engines they use, and filter by:",
						list: ["Engine manufacturer", "Engine type/subtype"],
						visual: {
							title: "List of Maintenance Directors at operators using General Electric engines",
							url: "/static/img/product/business-jet-operator-contact-data/contact-search-by-engines.jpg",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Get access to more than 44,000 business jet operators and over 14,000 airport profiles.",
						url: URL_BZA_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Business Jet Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 29,000 business jets currently being tracked.",
						url: URL_BZA_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into business jet aircraft owners, managers, and SPVs.",
						url: URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Aircraft History Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_BZA_AIRCRAFT_HISTORY_DATA,
					},
				]}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news
					and data for 14 days. Our no-commitment guarantee:"
					subscribeLink={subscribeLink}
				/>
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				downloadsDescription="Our Business Jet Contact Data is ready for instant, unlimited downloads in Excel, CSV or JSON format."
				list={[
					"14,000+ senior management contacts worldwide",
					"Stay up-to-date with management changes news updates",
				]}
				subscribeLink={subscribeLink}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news
					and data for 14 days. Our no-commitment guarantee:"
					subscribeLink={subscribeLink}
				/>
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"The Business Jet Operator Contact Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I use these contacts to reach out to executives?",
						content:
							"ch-aviation complies fully with the General Data Protection Regulation (GDPR), Europe’s framework for data protection that replaced the 1995 EU Data Protection Directive (DPD). All of our contact data is sourced from publicly available information, meaning we do not have explicit consent. However, this data is collected based on ch-aviation and its customers' legitimate interest in obtaining contact information for businesses in the airline industry. We recommend consulting your data protection advisers to determine the appropriate use of this contact information, including the type of emails you send. Many of ch-aviation's customers, in collaboration with their data protection advisers, have aligned with our legal team's conclusion that sharing names, email addresses, and social media profiles in a B2B context complies with GDPR.",
					},
					{
						title: "How often is the data updated?",
						content:
							"The operator contacts are being updated on a monthly basis, ensuring you always have the latest information.",
					},
					{
						title: "What if I notice the information on your website is outdated?",
						content:
							"Given the fast-paced nature of the industry, changes are inevitable, and there may be instances where we cannot update our data immediately. If you come across outdated contact details or job positions on our website, we kindly encourage you to click the 'Incorrect' button next to the relevant name. This notifies our team, allowing us up to seven days to verify and provide the updated information. Your assistance helps us maintain the accuracy and reliability of our database, and we greatly appreciate your support.",
					},
				]}
			/>
		</>
	);
}
